import React, { useEffect, useRef, useState } from 'react';
import {
  Card,
  Col,
  Row,
  Table,
  Tag,
  Typography,
  Dropdown,
  Menu,
  Button,
  Space,
  Input,
  Alert,
  message,
} from 'antd';
import {
  FilterOutlined,
  CloseOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useGetPreApprovalQuery } from '../../apis/dealAPI';
import { formatDate } from '@/utils/commonUtils';
import TableTitle from '@/components/Elements/Heading/TableTitle';
import BuyersAgentHubSpotForm from '@/components/HubspotForm/BuyersAgentHubspotForm';
import { useResendRegisterEmailMutation } from '@/store/apis/authApi';
import { useAppSelector } from '@/hooks/hooks';
import { Grid } from 'antd';

const PreapprovalPage = () => {
  const { useBreakpoint } = Grid;

  const { xs, lg } = useBreakpoint();

  const [dealStageFilter, setDealStageFilter] = useState<string | null>(null);

  // Fetch pre-approval data
  const {
    data: preApprovalDataList,
    isLoading: preApprovalLoading,
    error: preApprovalError,
  } = useGetPreApprovalQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  const [resendRegisterEmail, { isLoading: isResending }] =
    useResendRegisterEmailMutation();

  const hasShownErrorRef = useRef(false);

  const [emailNotVerified, setEmailNotVerified] = useState(false);

  useEffect(() => {
    if (preApprovalError && !hasShownErrorRef.current) {
      let message = '';
      if (
        'data' in preApprovalError &&
        typeof preApprovalError.data === 'object' &&
        preApprovalError.data !== null
      ) {
        message = (preApprovalError.data as any).error || '';
        console.log(message);
      }
      if (message.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true);
      }
      // displayError(preApprovalError);
      hasShownErrorRef.current = true;
    }
  }, [preApprovalError]);

  const dealStages = Array.from(
    new Set(
      preApprovalDataList?.data?.map((item: any) => item.deal_stage_name) || [],
    ),
  ) as string[];

  const columns: any = [
    // {
    //   title: <TableTitle>S.N</TableTitle>,
    //   key: 'index',
    //   align: 'center',
    //   render: (_: any, __: any, index: number) => (
    //     <Typography.Text strong>{index + 1}</Typography.Text>
    //   ),
    //   width: 100,
    // },
    {
      title: <TableTitle>Object ID</TableTitle>,
      dataIndex: 'hs_object_id',
      key: 'hs_object_id',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    // {
    //   title: <TableTitle>Amount</TableTitle>,
    //   dataIndex: 'amount',
    //   key: 'amount',
    //   align: 'center',
    //   render: (text: string) => (
    //     <Typography.Text>{text || 'N/A'}</Typography.Text>
    //   ),
    //   width: 200,
    // },
    {
      title: <TableTitle>Deal Name</TableTitle>,
      dataIndex: 'dealname',
      key: 'dealname',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    {
      title: <TableTitle>Lender</TableTitle>,
      dataIndex: 'lender',
      key: 'lender',
      align: 'center',
      render: (text: string) => (
        <Typography.Text>{text || 'N/A'}</Typography.Text>
      ),
      width: 200,
    },
    {
      title: <TableTitle>Deal Stage</TableTitle>,
      dataIndex: 'deal_stage_name',
      key: 'deal_stage_name',
      align: 'center',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }: any) => (
        <div style={{ padding: 16, width: 250 }}>
          <Typography.Text strong>Filter by Deal Stage</Typography.Text>
          <div style={{ marginTop: 12 }}>
            {dealStages.map((stage) => (
              <Button
                key={stage}
                type={selectedKeys[0] === stage ? 'primary' : 'default'}
                block
                style={{
                  marginBottom: 8,
                  textAlign: 'left',
                  backgroundColor:
                    selectedKeys[0] === stage ? undefined : '#f5f5f5',
                }}
                onClick={() => {
                  setSelectedKeys(stage ? [stage] : []);
                  setDealStageFilter(stage);
                }}
              >
                {stage}
              </Button>
            ))}
          </div>
          <Space
            style={{
              marginTop: 12,
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              icon={<CloseOutlined />}
              onClick={() => {
                clearFilters();
                setDealStageFilter(null);
                confirm();
              }}
            >
              Reset
            </Button>
            <Button
              type="primary"
              icon={<CheckOutlined />}
              onClick={() => confirm()}
            >
              Apply
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: string, record: any) =>
        record.deal_stage_name === value,
      render: (_: any, { deal_stage_name }: { deal_stage_name: string }) => {
        const color = deal_stage_name === 'in_progress' ? 'orange' : 'green';
        return (
          <Tag color={color} style={{ textAlign: 'center', display: 'block' }}>
            {deal_stage_name.toUpperCase()}
          </Tag>
        );
      },
      width: 200,
    },
    {
      title: <TableTitle>Create Date</TableTitle>,
      dataIndex: 'createdate',
      key: 'createdate',
      align: 'center',
      render: (_: any, { createdate }: { createdate: string | null }) =>
        createdate ? (
          <Typography.Text>{formatDate(createdate)}</Typography.Text>
        ) : (
          <Typography.Text>N/A</Typography.Text>
        ),
      width: 200,
    },
    {
      title: <TableTitle>Pre-approval Expiry Date</TableTitle>,
      dataIndex: 'aip_expiry',
      key: 'aip_expiry',
      align: 'center',
      render: (_: any, { aip_expiry }: { aip_expiry: string | null }) =>
        aip_expiry ? (
          <Typography.Text>{formatDate(aip_expiry)}</Typography.Text>
        ) : (
          <Typography.Text>N/A</Typography.Text>
        ),
      width: 300,
    },
  ];

  const { userData } = useAppSelector((state) => state.auth);

  const handleSendVerification = () => {
    message.loading('Sending verification email...');
    resendRegisterEmail({ email: userData?.email || '' });
  };

  return (
    <div style={{ width: '100%' }}>
      {emailNotVerified && (
        <Alert
          banner
          message={
            <Space>
              <Typography.Text>
                Your email is not verified. Please verify it to access
                pre-approval HubSpot data.
              </Typography.Text>
              <Button
                type="link"
                style={{
                  cursor: 'pointer',
                  color: '#1890ff',
                  textDecoration: 'underline',
                  fontSize: 14,
                  fontWeight: 'bold',
                }}
                onClick={handleSendVerification}
                loading={isResending}
              >
                Verify your email
              </Button>
            </Space>
          }
          type="warning"
          showIcon
          style={{
            marginTop: '0.5rem',
            padding: xs ? '5px 7px' : '8px 12px',
          }}
        />
      )}
      <Row gutter={[16, 16]} style={{ marginTop: '1rem' }}>
        <Col xs={24} md={24}>
          <Card
            style={{
              marginBottom: '0.5rem',
            }}
            title="Pre-approval Applications"
            extra={
              dealStageFilter && (
                <Tag
                  closable
                  onClose={() => {
                    setDealStageFilter(null);
                  }}
                >
                  {dealStageFilter}
                </Tag>
              )
            }
          >
            <Table
              bordered
              dataSource={
                dealStageFilter
                  ? preApprovalDataList?.data?.filter(
                      (item: any) => item.deal_stage_name === dealStageFilter,
                    ) || []
                  : preApprovalDataList?.data || []
              }
              columns={columns}
              rowKey="id"
              loading={preApprovalLoading}
              pagination={{ pageSize: 5 }}
              scroll={{
                x: 'max-content',
              }}
            />
          </Card>
        </Col>
        <Col md={24} xl={12}>
          <BuyersAgentHubSpotForm />
        </Col>
      </Row>
    </div>
  );
};

export default PreapprovalPage;
